/**
 * The hub's HTTP client. Same origin always (Vite proxies /api in
 * development), so the session cookie simply travels with every request.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
  ) {
    super(message);
  }
}

export async function api<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method: init.method ?? "GET",
    credentials: "same-origin",
    headers: init.body === undefined ? undefined : { "Content-Type": "application/json" },
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
  });
  if (res.status === 204) return undefined as T;
  const data = (await res.json().catch(() => ({}))) as { error?: string; code?: string };
  if (!res.ok) throw new ApiError(res.status, data.error ?? `Request failed (${res.status})`, data.code);
  return data as T;
}

export type Role = "starter" | "pro" | "admin";

export interface PublicUser {
  id: string;
  username: string;
  displayName: string;
  role: Role;
  bio: string;
  avatarSeed: string;
  isDemo: boolean;
  isSimulated: boolean;
  createdAt: string;
  /** "verified" for a Verified publisher. */
  publisherBadge: "verified" | null;
}

export interface SessionUser extends PublicUser {
  isPublic: boolean;
  canSwitchLayout: boolean;
}

export interface PersonaCard {
  key: "sam" | "alex" | "admin";
  displayName: string;
  role: Role;
  tagline: string;
  avatarSeed: string;
}

export interface AuthConfig {
  /** The one-click demo accounts; empty unless the operator turned them on. */
  personas: PersonaCard[];
}
